import type { GetDocumentByPathQuery } from '#src/graphql/generated';
import type { EditorJsEditorHandle } from './editor';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DocumentChatComposerProvider } from './document-chat-composer-context';
import { DocumentChatFooter } from './document-chat-footer';
import { DOCUMENT_EDITOR_MOBILE_MAX_PX } from './document-editor-layout';
import { DocumentMermaidVisibilityProvider } from './document-mermaid-visibility-context';
import { EditDocumentProvider } from './edit-document-context';
import { normalizeDocumentLinksInEditorJsData } from './editor-document-links';
import {
  type EditorJsData,
  editorJsStableStringForDirtyCompare,
  editorJsStableStringForNewDocPlaceholderCompare,
  parseEditorJs,
  stringifyEditorJs,
} from './editorjs-types';
import { ViewDocumentHeader } from './view-document-header';
import { ViewDocumentViewer } from './view-document-viewer';

import { useMediaMaxWidth } from '#src/hooks/use-media-max-width';
import { useAppConfig } from '#src/providers/app-config-provider';
import { useAppSettings } from '#src/providers/app-settings-provider';
import { useCurrentUser } from '#src/providers/user-provider';
import { formatDocumentName } from '#src/utils/format-document-name';

export type DocumentByPathResult = GetDocumentByPathQuery['documentByPath'];

interface ViewDocumentProps {
  /** Document from parent query (Document or DocumentNotFound). */
  document: DocumentByPathResult;
  /** Canonical path (document path when found, else requested path from URL). */
  path: string;
  refetch: () => Promise<unknown>;
}

/** Path from DocumentResult (Document or DocumentNotFound). */
function getPathFromResult(
  result: DocumentByPathResult,
  fallback: string,
): string {
  return result?.__typename === 'Document'
    ? result.path
    : result?.__typename === 'DocumentNotFound'
      ? (result.requestedPath ?? fallback)
      : fallback;
}

/** Part of child path after parent path (for display). */
export function ViewDocument({ document, path, refetch }: ViewDocumentProps) {
  const navigate = useNavigate();
  const { config, recordRecentlyOpenedDocument } = useAppConfig();
  const { documentPathRegex } = useAppSettings();
  const user = useCurrentUser();

  const isNewDocument = document?.__typename === 'DocumentNotFound';
  const documentId =
    document?.__typename === 'Document' ? document.id : undefined;
  const displayPath = getPathFromResult(document, path);

  const [isCreateSubpageOpen, setIsCreateSubpageOpen] = useState(false);

  const isMobileEditorLayout = useMediaMaxWidth(DOCUMENT_EDITOR_MOBILE_MAX_PX);
  const [composerFooterVisible, setComposerFooterVisible] = useState(
    () => !isMobileEditorLayout,
  );

  useEffect(() => {
    if (isMobileEditorLayout) {
      setComposerFooterVisible(false);
    } else {
      setComposerFooterVisible(true);
    }
  }, [isMobileEditorLayout]);

  const toggleComposerFooter = useCallback(() => {
    setComposerFooterVisible((v) => !v);
  }, []);

  const childrenItems =
    (document?.children?.items as Array<{ id: string; path: string }> | null) ??
    [];
  const childrenHasMore = document?.children?.pagination?.hasMore ?? false;

  useEffect(() => {
    if (documentId) {
      recordRecentlyOpenedDocument(documentId, path);
    }
  }, [documentId, path, recordRecentlyOpenedDocument]);

  const editorRef = useRef<EditorJsEditorHandle | null>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const documentValue =
    document?.__typename === 'Document' ? document.value : null;

  const [editorInitialData] = useState(() => {
    const parsed = documentValue
      ? (parseEditorJs(documentValue) ?? null)
      : null;
    const origin =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    const base: EditorJsData = parsed?.blocks?.length
      ? parsed
      : (() => {
          const segments = formatDocumentName(displayPath);
          const title =
            segments.length > 0
              ? segments[segments.length - 1]
              : displayPath || 'Untitled';
          return {
            time: Date.now(),
            blocks: [{ type: 'header', data: { text: title, level: 1 } }],
            version: '2.0.0',
          };
        })();
    return normalizeDocumentLinksInEditorJsData(
      base,
      documentPathRegex,
      origin,
    );
  });

  const newDocPlaceholderBaselineRef = useRef(
    editorJsStableStringForNewDocPlaceholderCompare(editorInitialData),
  );
  const [editorPlaceholderMuted, setEditorPlaceholderMuted] =
    useState(isNewDocument);

  const [lastSavedValue, setLastSavedValue] = useState(
    stringifyEditorJs(editorInitialData),
  );
  /** Increments when serialized editor content changes while dirty (for auto-save debounce). */
  const [dirtyEditGeneration, setDirtyEditGeneration] = useState(0);
  const prevSerializedForAutoSaveRef = useRef(
    editorJsStableStringForDirtyCompare(editorInitialData),
  );

  // Sync lastSavedValue when document loads or changes externally (e.g. initial load)
  useEffect(() => {
    setLastSavedValue(stringifyEditorJs(editorInitialData));
  }, [editorInitialData]);

  useEffect(() => {
    prevSerializedForAutoSaveRef.current = editorJsStableStringForDirtyCompare(
      parseEditorJs(lastSavedValue) ?? null,
    );
  }, [lastSavedValue]);

  const onNavigateToDocument = useCallback(
    (docPath: string) => {
      navigate(`/documents/${docPath}`);
    },
    [navigate],
  );

  const revertToLastSavedValue = useCallback(() => {
    const savedData = parseEditorJs(lastSavedValue) ?? null;
    editorRef.current?.resetContent(savedData);
    setHasChanges(false);
  }, [lastSavedValue]);

  const openCreateSubpagePrompt = useCallback(() => {
    setIsCreateSubpageOpen(true);
  }, []);

  // Note: handleFilePaste removed; complex format no longer supports raw paste → markdown mapping here.

  // Track changes in the editor
  useEffect(() => {
    const checkForChanges = () => {
      const editorData = editorRef.current?.getValue() ?? null;
      const currentStable = editorJsStableStringForDirtyCompare(editorData);
      const savedStable = editorJsStableStringForDirtyCompare(
        parseEditorJs(lastSavedValue) ?? null,
      );
      const dirty = currentStable !== savedStable;
      setHasChanges(dirty);
      if (currentStable !== prevSerializedForAutoSaveRef.current) {
        prevSerializedForAutoSaveRef.current = currentStable;
        if (dirty) {
          setDirtyEditGeneration((n) => n + 1);
        }
      }

      if (isNewDocument) {
        const stillDefaultPlaceholder =
          editorData == null ||
          editorJsStableStringForNewDocPlaceholderCompare(editorData) ===
            newDocPlaceholderBaselineRef.current;
        setEditorPlaceholderMuted((prev) =>
          prev === stillDefaultPlaceholder ? prev : stillDefaultPlaceholder,
        );
      } else {
        setEditorPlaceholderMuted((prev) => (prev === false ? prev : false));
      }
    };

    // Check periodically for changes
    const interval = setInterval(checkForChanges, 500);
    return () => clearInterval(interval);
  }, [isNewDocument, lastSavedValue]);

  return (
    <DocumentMermaidVisibilityProvider documentPath={displayPath}>
      <EditDocumentProvider editorRef={editorRef}>
        <DocumentChatComposerProvider>
          <ViewDocumentHeader
            document={document}
            path={path}
            displayPath={displayPath}
            documentId={documentId}
            apiHost={config.apiHost}
            userEmail={user.email}
            isNewDocument={isNewDocument}
            editorPlaceholderMuted={editorPlaceholderMuted}
            childrenItems={childrenItems}
            childrenHasMore={childrenHasMore}
            onNavigateToHistory={() =>
              navigate(`/documents/${displayPath}/history`)
            }
            hasChanges={hasChanges}
            dirtyEditGeneration={dirtyEditGeneration}
            getCurrentValue={() =>
              stringifyEditorJs(editorRef.current?.getValue() ?? null)
            }
            refetch={refetch}
            onAfterSave={(savedValueString: string) => {
              const savedData = parseEditorJs(savedValueString) ?? null;
              editorRef.current?.resetContent(savedData);
              setLastSavedValue(savedValueString);
              setHasChanges(false);
            }}
            onRevertToSaved={revertToLastSavedValue}
            isCreateSubpageOpen={isCreateSubpageOpen}
            onCreateSubpageOpenChange={setIsCreateSubpageOpen}
            isMobileEditorLayout={isMobileEditorLayout}
            composerFooterVisible={composerFooterVisible}
            onComposerFooterToggle={toggleComposerFooter}
          />

          <ViewDocumentViewer
            editorRef={editorRef}
            documentId={documentId}
            isNewDocument={isNewDocument}
            editorPlaceholderMuted={editorPlaceholderMuted}
            path={path}
            displayPath={displayPath}
            value={editorInitialData}
            apiHost={config.apiHost}
            userEmail={user.email}
            children={childrenItems}
            onCreateSubpage={openCreateSubpagePrompt}
            onNavigateToDocument={onNavigateToDocument}
          />
          <DocumentChatFooter
            documentId={documentId}
            isNewDocument={isNewDocument}
            active={composerFooterVisible}
          />
        </DocumentChatComposerProvider>
      </EditDocumentProvider>
    </DocumentMermaidVisibilityProvider>
  );
}
